import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { parsePhoneNumber } from 'libphonenumber-js';
import { useHistory, useLocation } from 'react-router-dom';
import 'firebase/auth';
import firebase from 'firebase/app';
import moment from 'moment';
import { debounce } from 'lodash';
import classNames from 'classnames';

import { Button, BUTTON_SIZE, BUTTON_TYPE } from '../Button';
import DisclaimerPublic from '../DisclaimerPublic';
import DotsLoader from '../DotsLoader';
import Input from '../InputNew';

import {
  REGISTER_FROM_PARAM, REGISTER_OPTIN_PARAM, REGISTER_SIGNED_AT_PARAM,
  REGISTER_SIGNUP_PARAM, REGISTER_SOURCE_PARAM, REGISTER_TOPIC_PARAM,
  REGISTER_PARTNER_ID_PARAM, REGISTER_PARTNER_NAME_PARAM, REGISTER_ORIGIN_URL_PARAM,
} from '../../config/routes';
import {
  PRIVACY_POLICY_URL,
  SUPPORT_URL,
  TERMS_OF_SERVICE_URL,
} from '../../config/externalURL';
import { getTrackerId, POSTHOG_CAPTURE_ATTRIBUTES, TRACKER_IDS } from '../../config/tracker';
import {
  getUserStatus,
  updateUser,
  postPhoneLogin,
  sendEmailVerification,
  sendPasswordResetEmail,
  createOrFetchUser,
  forceResetPassword,
} from '../../utils/service';
import { getReferrerOrigin } from '../../utils/url';
import GoogleLogo from '../../images/google-logo.svg';
import IconSend from '../../images/icon-send.svg';
import IconCloudy from '../../images/icon-cloudy.svg';
import IconEnvelope from '../../images/icon-envelope.svg';
import IconHourglass from '../../images/icon-hourglass.svg';
import IconMail from '../../images/mail-icon.svg';
import TrashieEyesRight from '../../images/trashie-eyes-right.png';
import {
  getGenericError,
  isAuthCredentialsError,
  isInvalidActionError,
  isTooManyRequestsError,
  isInvalidVerificationCodeError,
  isCodeExpiredError,
} from '../../utils/errors';
import { getNextButtonStyles, getOptionButtonStyles } from '../../utils/tbbRegistration';
import { SIGNUP_METHOD, SOURCE, TOPIC_SIGNUP } from '../../utils/login';
import { getSearchParam } from '../../utils/routes';
import { useAlert } from '../../../providers/AlertProvider';
import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';
import {
  signIn,
  signInWithGoogle,
  sendPhoneOtp,
  confirmPhoneOtp,
  clearRecaptchaVerifier,
  restoreAuthSession,
} from '../../../../utils/auth';
import { markTokenAsRefreshed } from '../../../../api/crud';
import safeStorage from '../../utils/localStorage';
import NavigationBar from '../NavigationBar/NavigationBar';
import { emailFormat } from '../../utils/emailFormatValidator';
import GeneralModal from '../GeneralModal';
import LoadingBar from '../LoadingBar';
import MessageContent from '../MessageContent';
import OptIn from '../OptIn';
import InputPin from '../InputPin';
import PhoneVerification from '../PhoneVerification';
import SignupForm from './SignupForm';

import './GlobalLoginV2.scss';

export const viewType = {
  VIEW_EMAIL_FORM: 'emailForm',
  VIEW_EMAIL_PASSWORD_FORM: 'emailAndPassword',
  VIEW_EMAIL_PASSWORD_SIGNUP_FORM: 'emailPasswordSignup',
  VIEW_LINK_SENT: 'linkSent',
  VIEW_LINK_CONFIRM: 'linkConfirm',
  VIEW_SENDING_LINK: 'sendingLink',
  VIEW_VERIFY_EMAIL: 'verifyEmail',
  VIEW_PHONE_SIGNUP: 'phoneSignup',
  VIEW_PHONE_SIGNUP_EMAIL: 'phoneSignupEmail',
  VIEW_PHONE_LINK_LOGIN: 'phoneLinkLogin',
  VIEW_PHONE_VERIFICATION: 'phoneVerification',
  VIEW_PHONE_OTP_LOGIN: 'phoneOtpLogin',
  VIEW_FORCE_RESET_PASSWORD: 'forceResetPassword',
  VIEW_NAME_FORM: 'nameForm',
  VIEW_SESSION_TIMEOUT: 'sessionTimeout',
};

const RESEND_COUNTDOWN_SECONDS = 60;

const NAME_FORM_CHANNEL_NAME = 'name-form';
const NAME_FORM_MSG = {
  COMPLETED: 'COMPLETED',
  SKIPPED: 'SKIPPED',
};

const LINK_ERROR_DEFAULT = {
  image: undefined,
  title: undefined,
  subtitle: undefined,
  cta: {
    text: undefined,
    onClick: undefined,
  },
};

const MODAL_CONTENT_DEFAULT = {
  image: undefined,
  title: undefined,
  subtitle: undefined,
  cta: {
    text: undefined,
    onClick: undefined,
  },
};

const LOCAL_STORAGE_EMAIL_KEY = 'emailForSignIn';
const SIGNUP_PHONE_STORAGE_KEY = 'signupPhoneForVerification';

const {
  login: {
    passwordButton: trackerPasswordButton,
    googleButton: trackerGoogleButton,
  },
} = TRACKER_IDS;

const getReturnPath = () => window.location.pathname + window.location.search +
 window.location.hash;

const isValidPhone = (phoneNumber) => {
  const digits = (phoneNumber || '').replace(/\D/g, '');
  return digits.length >= 10;
};

const GlobalLogin = ({
  initialEmail = '',
  onViewChange,
  isParentLoading,
  storeConfig = {},
  setFinishProcessData,
  isEmbedded,
  isExternalEmbedded = false,
  view,
  setView,
  showLogo = true,
  onPhoneVerificationDone,
  onPhoneVerificationSkipped,
  onForcePwdResetComplete,
  onNameFormDone,
  onNameFormSkipped,
}) => {
  const history = useHistory();
  const { search } = useLocation();
  const validRef = useRef(null);
  const setAlert = useAlert();
  const { pauseAuthListener, resumeAuthListener } = useFirebaseAuth();

  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [modalContent, setModalContent] = useState(MODAL_CONTENT_DEFAULT);
  const [linkError, setLinkError] = useState(LINK_ERROR_DEFAULT);
  const [isUserEmail, setIsUserEmail] = useState(false);
  const [isVerifyingUserEmail, setIsVerifyingUserEmail] = useState(false);
  const [optIn, setOptIn] = useState(true);
  const [phone, setPhone] = useState('');
  const [countryCode] = useState('US');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isVerifyingUserPhone, setIsVerifyingUserPhone] = useState(false);
  const [isUserPhone, setIsUserPhone] = useState(false);
  const [phoneLoginError, setPhoneLoginError] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);
  const [pinValues, setPinValues] = useState(Array(6).fill(''));
  const [isValidPin, setIsValidPin] = useState(true);
  const resendTimerRef = useRef(null);
  const phoneValidRef = useRef(null);
  const isCheckingVerificationRef = useRef(false);
  const confirmationResultRef = useRef(null);
  const phoneOtpOwnedRef = useRef(false);
  const sessionTimeoutRef = useRef(null);
  const sessionTimeoutAbortRef = useRef(false);
  const isForceResetSubmittingRef = useRef(false);
  // tracks last-changed field ('email' | 'phone') to prevent autocomplete race conditions
  const activeFieldRef = useRef(null);
  // timestamp of last email change — email takes priority over phone on simultaneous autocomplete
  const emailLastChangedRef = useRef(0);

  const loading = useMemo(() => (isParentLoading || isLoading), [isParentLoading, isLoading]);
  const isNewUser = useMemo(
    () => emailFormat(email) && !isUserEmail && !isVerifyingUserEmail,
    [email, isUserEmail, isVerifyingUserEmail],
  );

  const signupPhoneForVerification = useMemo(() => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length >= 10) return digits;
    return safeStorage.getItem(SIGNUP_PHONE_STORAGE_KEY) || '';
  }, [phone]);

  const startResendCountdown = useCallback(() => {
    setResendCountdown(RESEND_COUNTDOWN_SECONDS);
    if (resendTimerRef.current) {
      clearInterval(resendTimerRef.current);
    }
    resendTimerRef.current = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(resendTimerRef.current);
          resendTimerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleResendVerificationEmail = useCallback(async () => {
    try {
      const userEmail = email || firebase.auth().currentUser?.email;
      if (userEmail) {
        const { error } = await sendEmailVerification({
          returnUrl: getReturnPath(),
        });
        if (error) {
          throw error;
        }
        startResendCountdown();
      }
    } catch (error) {
      setAlert({
        type: 'notification',
        message: 'Unable to resend verification email. Please try again.',
        error,
      });
    }
  }, [email, startResendCountdown, setAlert]);

  const handleChangeEmail = useCallback(async () => {
    try {
      // User must sign out first, otherwise Login.jsx forces VIEW_VERIFY_EMAIL again.
      const { currentUser } = firebase.auth();
      if (currentUser) {
        await firebase.auth().signOut();
      }
    } catch (error) {
      setAlert({
        type: 'notification',
        message: 'Unable to change email right now. Please try again.',
        error,
      });
    }

    setErrorMessage('');
    setPhoneLoginError('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setPhone('');
    setIsUserEmail(false);
    setIsVerifyingUserPhone(false);
    setIsUserPhone(false);
    if (resendTimerRef.current) {
      clearInterval(resendTimerRef.current);
      resendTimerRef.current = null;
    }
    setResendCountdown(0);
    safeStorage.removeItem(SIGNUP_PHONE_STORAGE_KEY);
    setFinishProcessData?.({});
    setView(viewType.VIEW_EMAIL_FORM);
  }, [setAlert, setFinishProcessData, setView]);

  const handleFinish = useCallback((
    userRegistered,
    subscribeEmail,
    userRegisteredMethod,
  ) => {
    setFinishProcessData({
      userRegistered,
      userRegisteredMethod,
      subscribeEmail,
      optIn,
      search,
    });
  }, [optIn, search]);

  const handleRequestNewLink = useCallback(() => {
    setErrorMessage('');
    setLinkError(LINK_ERROR_DEFAULT);
    setView(viewType.VIEW_EMAIL_FORM);
  }, []);

  const handleUserLogin = useCallback(async () => {
    if (!email || !password) {
      return;
    }

    setErrorMessage('');
    setIsLoading(true);

    try {
      await signIn(email, password);
      handleFinish(false, email);
    } catch (error) {
      if (isAuthCredentialsError(error)) {
        setErrorMessage('Invalid email or password. Please check your credentials and try again.');
        return;
      }

      if (isTooManyRequestsError(error)) {
        setErrorMessage('Access to this account has been temporarily disabled due to many failed login attempts. Please try again later.');
        return;
      }

      setErrorMessage(getGenericError());
    } finally {
      setIsLoading(false);
    }
  }, [email, password, handleFinish]);

  const handleSignInWithGoogle = useCallback(async () => {
    try {
      safeStorage.setItem('signUp_method', SIGNUP_METHOD.GOOGLE);
      safeStorage.setItem('optIn', optIn);
      const user = await signInWithGoogle();

      handleFinish(
        user?.additionalUserInfo?.isNewUser,
        user?.additionalUserInfo?.profile?.email,
        SIGNUP_METHOD.GOOGLE,
      );
    } catch (error) {
      setAlert({
        type: 'notification', // TODO
        message: 'Unable to sign in with google',
        error,
      });
    }
  }, [handleFinish, optIn]);

  const signInWithEmailLink = useCallback(async (emailForSignIn) => {
    setIsLoading(true);

    firebase.auth().signInWithEmailLink(emailForSignIn, window.location.href)
      .then(async () => {
        // On signInWithEmailLink the parent component MUST handle specific logic in encoded data
        handleFinish();
      })
      .catch((error) => {
        const isInvalidLink = isInvalidActionError(error);

        setLinkError({
          image: IconCloudy,
          title: isInvalidLink ? 'THIS LOGIN LINK IS INVALID' : 'WE’VE ENCOUNTERED DIFFICULTIES WHILE VERIFYING THIS LINK',
          cta: {
            text: isInvalidLink ? 'SEND A NEW LINK' : 'TRY AGAIN',
            onClick: isInvalidLink ? handleRequestNewLink : () => window.location.reload(),
          },
        });
        setView(viewType.VIEW_LINK_SENT);
      })
      .finally(() => {
        setIsLoading(false);
        safeStorage.removeItem(LOCAL_STORAGE_EMAIL_KEY);
      });
  }, [handleFinish, handleRequestNewLink]);

  const getUserEmailStatus = useCallback(userEmail => {
    setIsVerifyingUserEmail(true);

    const validateUserEmailStatus = async (validateUserEmail) => {
      if (activeFieldRef.current !== 'email') {
        setIsVerifyingUserEmail(false);
        return;
      }

      setIsVerifyingUserEmail(true);

      if (validateUserEmail && emailFormat(validateUserEmail)) {
        const {
          data: userStatusData,
          error: userStatusError,
        } = await getUserStatus(validateUserEmail);

        setIsUserEmail(!userStatusError && userStatusData?.exists);
        setIsVerifyingUserEmail(false);
        return;
      }

      setIsUserEmail(false);
      setIsVerifyingUserEmail(false);
    };

    if (validRef.current && validRef.current.cancel) {
      validRef.current.cancel();
    }

    validRef.current = debounce(validateUserEmailStatus, 700);

    validRef.current(userEmail);
  }, []);

  const getUserPhoneStatus = useCallback((userPhone) => {
    setIsVerifyingUserPhone(true);

    const validateUserPhoneStatus = async (validatePhone) => {
      if (activeFieldRef.current !== 'phone') {
        setIsVerifyingUserPhone(false);
        return;
      }

      setIsVerifyingUserPhone(true);

      if (validatePhone && isValidPhone(validatePhone)) {
        const cleanPhone = validatePhone.replace(/\D/g, '');
        const {
          data: userStatusData,
          error: userStatusError,
        } = await getUserStatus(null, cleanPhone, countryCode);

        if (userStatusError) {
          setIsUserPhone(false);
          setIsVerifyingUserPhone(false);
          return;
        }

        const phoneExists = userStatusData?.exists;
        setIsUserPhone(phoneExists);
        setIsVerifyingUserPhone(false);

        if (!phoneExists) {
          safeStorage.setItem(SIGNUP_PHONE_STORAGE_KEY, cleanPhone);
          setView(viewType.VIEW_PHONE_SIGNUP);
        }
        return;
      }

      setIsUserPhone(false);
      setIsVerifyingUserPhone(false);
    };

    if (phoneValidRef.current && phoneValidRef.current.cancel) {
      phoneValidRef.current.cancel();
    }

    phoneValidRef.current = debounce(validateUserPhoneStatus, 700);

    phoneValidRef.current(userPhone);
  }, [countryCode]);

  const handleOnEmailChange = useCallback(async (newValue) => {
    activeFieldRef.current = 'email';
    emailLastChangedRef.current = Date.now();
    setEmail(newValue);
    setErrorMessage(newValue && !emailFormat(newValue) ? 'Please enter a valid email to continue' : '');
    getUserEmailStatus(newValue);
  }, [getUserEmailStatus]);

  const handleOnPhoneChange = useCallback((newValue) => {
    // Give email priority: if email was changed within 100ms, it's a simultaneous autocomplete
    if (Date.now() - emailLastChangedRef.current > 100) {
      activeFieldRef.current = 'phone';
    }
    setPhone(newValue);
    setErrorMessage('');
    setPhoneLoginError('');
    setIsUserPhone(false);
    getUserPhoneStatus(newValue);
  }, [getUserPhoneStatus]);

  const handleOnPasswordChange = useCallback((newValue) => {
    setErrorMessage('');
    setPassword(newValue);
  }, []);

  const handleOnConfirmPasswordChange = useCallback((newValue) => {
    setErrorMessage('');
    setConfirmPassword(newValue);
  }, []);

  const handleContinueWithPassword = useCallback(() => {
    setErrorMessage('');
    setView(viewType.VIEW_EMAIL_PASSWORD_FORM);
  }, []);

  const handleContinueToSignup = useCallback(() => {
    setErrorMessage('');
    setPassword('');
    setConfirmPassword('');
    setView(viewType.VIEW_EMAIL_PASSWORD_SIGNUP_FORM);
  }, []);

  const handlePhoneGoBack = useCallback(() => {
    setErrorMessage('');
    setPhoneLoginError('');
    setIsVerifyingUserPhone(false);
    setIsUserPhone(false);
    setPhone('');
    safeStorage.removeItem(SIGNUP_PHONE_STORAGE_KEY);
    setView(viewType.VIEW_EMAIL_FORM);
  }, []);

  const handlePhoneLogin = useCallback(async () => {
    setErrorMessage('');
    setPhoneLoginError('');
    setIsLoading(true);

    try {
      const fullPhone = `+1${phone.replace(/\D/g, '')}`;
      const parsed = parsePhoneNumber(fullPhone, 'US');

      if (!parsed || !parsed.isValid()) {
        setErrorMessage('Please enter a valid phone number.');
        setIsLoading(false);
        return;
      }

      pauseAuthListener();
      phoneOtpOwnedRef.current = true;

      const result = await sendPhoneOtp(parsed.number);
      confirmationResultRef.current = result;

      setPinValues(Array(6).fill(''));
      setIsValidPin(true);
      setIsLoading(false);
      setView(viewType.VIEW_PHONE_OTP_LOGIN);
      startResendCountdown();
    } catch (error) {
      resumeAuthListener();
      phoneOtpOwnedRef.current = false;
      setIsLoading(false);
      setPhoneLoginError('Failed to send verification code. Please try again.');
    }
  }, [phone, pauseAuthListener, resumeAuthListener, startResendCountdown]);

  const handlePhoneOtpResend = useCallback(async () => {
    setErrorMessage('');
    setIsValidPin(true);
    setPinValues(Array(6).fill(''));
    setIsLoading(true);

    try {
      if (!phoneOtpOwnedRef.current) {
        pauseAuthListener();
        phoneOtpOwnedRef.current = true;
      }

      const fullPhone = `+1${phone.replace(/\D/g, '')}`;
      const result = await sendPhoneOtp(fullPhone);
      confirmationResultRef.current = result;
      startResendCountdown();
    } catch (error) {
      setErrorMessage('Failed to resend code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [phone, pauseAuthListener, startResendCountdown]);

  const handlePhoneOtpValidate = useCallback(async () => {
    const code = pinValues.join('');

    if (code.length !== 6) {
      setIsValidPin(false);
      setErrorMessage('Please enter the full 6-digit code.');
      return;
    }

    setErrorMessage('');
    setIsValidPin(true);
    setIsLoading(true);

    try {
      const idToken = await confirmPhoneOtp(confirmationResultRef.current, code);

      const { data: loginData, error: loginError } = await postPhoneLogin({ idToken });

      if (loginError) {
        phoneOtpOwnedRef.current = false;
        resumeAuthListener();
        setErrorMessage('Oops, something happened on our side, no worries, please try again.');
        setIsLoading(false);
        return;
      }

      // Resume auth listener BEFORE restoring the session so that
      // onAuthStateChanged processes the custom token user and triggers
      // userData fetch. The isPhoneOnlyUser guard in FirebaseProvider
      // prevents the intermediate phone session from leaking through.
      phoneOtpOwnedRef.current = false;
      resumeAuthListener();

      await restoreAuthSession(loginData.customToken);

      setIsLoading(false);
      handleFinish(false, null, SIGNUP_METHOD.PHONE);
      setView(viewType.VIEW_EMAIL_FORM);
    } catch (error) {
      if (isInvalidVerificationCodeError(error)) {
        setIsValidPin(false);
        setErrorMessage('Invalid code. Verify it and try again or get a new code.');
      } else if (isCodeExpiredError(error)) {
        setIsValidPin(false);
        setErrorMessage('Code expired. Please request a new code.');
      } else {
        resumeAuthListener();
        phoneOtpOwnedRef.current = false;
        setErrorMessage(getGenericError());
      }
      setIsLoading(false);
    }
  }, [pinValues, resumeAuthListener, handleFinish]);

  const handlePhoneOtpGoBack = useCallback(() => {
    if (phoneOtpOwnedRef.current) {
      resumeAuthListener();
      phoneOtpOwnedRef.current = false;
    }
    setPinValues(Array(6).fill(''));
    setIsValidPin(true);
    setErrorMessage('');
    setResendCountdown(0);
    if (resendTimerRef.current) {
      clearInterval(resendTimerRef.current);
      resendTimerRef.current = null;
    }
    setView(viewType.VIEW_EMAIL_FORM);
  }, [resumeAuthListener]);

  const handlePhoneCreateAccount = useCallback(() => {
    setErrorMessage('');
    setView(viewType.VIEW_PHONE_SIGNUP_EMAIL);
  }, []);

  const handlePhoneLinkAccount = useCallback(() => {
    setErrorMessage('');
    setEmail('');
    setPassword('');
    setView(viewType.VIEW_PHONE_LINK_LOGIN);
  }, []);

  const handlePhoneLinkLogin = useCallback(async () => {
    if (!email || !password) {
      return;
    }

    try {
      setErrorMessage('');
      setIsLoading(true);
      await signIn(email, password);

      // TODO: Link phone number to the authenticated user account
      // After successful login, enroll the phone as a multi-factor method
      // using Firebase multi-factor authentication:
      // https://firebase.google.com/docs/auth/web/multi-factor
      // Phone: phone, Country code: countryCode

      setIsLoading(false);
      handleFinish(false, email);
    } catch (error) {
      setIsLoading(false);

      if (isAuthCredentialsError(error)) {
        setErrorMessage('Invalid email or password. Please check your credentials and try again.');
        return;
      }

      if (isTooManyRequestsError(error)) {
        setErrorMessage('Access to this account has been temporarily disabled due to many failed login attempts. Please try again later.');
        return;
      }

      setErrorMessage(getGenericError());
    }
  }, [email, password, phone, countryCode, handleFinish]);

  const handleForceResetPassword = useCallback(async () => {
    if (!password || !confirmPassword) {
      setErrorMessage('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setErrorMessage('Password must be at least 6 characters');
      return;
    }

    try {
      setErrorMessage('');
      setIsLoading(true);
      isForceResetSubmittingRef.current = true;

      const { error: resetError } = await forceResetPassword({ password });

      if (resetError) {
        setErrorMessage(resetError);
        setIsLoading(false);
        isForceResetSubmittingRef.current = false;
        return;
      }

      setPassword('');
      setConfirmPassword('');
      setIsLoading(false);
      sessionTimeoutAbortRef.current = true;
      isForceResetSubmittingRef.current = false;

      onForcePwdResetComplete?.();
    } catch (error) {
      setErrorMessage(getGenericError());
      setIsLoading(false);
      isForceResetSubmittingRef.current = false;
    }
  }, [password, confirmPassword, onForcePwdResetComplete]);

  const handleNameFormFinish = useCallback(async () => {
    setErrorMessage('');
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    setIsLoading(true);
    const payload = { firstName: trimmedFirst };
    if (trimmedLast) payload.lastName = trimmedLast;
    const { error: updateError } = await updateUser(payload);
    setIsLoading(false);
    if (updateError) {
      setErrorMessage(updateError);
      return;
    }
    try {
      const ch = new BroadcastChannel(NAME_FORM_CHANNEL_NAME);
      ch.postMessage({ type: NAME_FORM_MSG.COMPLETED });
      ch.close();
    } catch { /* ignore */ }
    onNameFormDone?.();
    setView(viewType.VIEW_EMAIL_FORM);
  }, [firstName, lastName, onNameFormDone]);

  const signUpTopic = useCallback(() => {
    if (isExternalEmbedded) {
      return TOPIC_SIGNUP.SIGN_UP_WEB_FLOW_LANDING_PAGE;
    }
    if (isEmbedded) {
      return TOPIC_SIGNUP.TBB_REGISTRATION_SIGNUP_FLOW;
    }
    return TOPIC_SIGNUP.SIGN_UP_WITH_LOGIN_LINK;
  }, [isExternalEmbedded, isEmbedded]);

  const handleSignupWithEmailPassword = useCallback(async () => {
    if (!email || !password || !confirmPassword) {
      setErrorMessage('Please fill in all fields');
      return;
    }

    if (!emailFormat(email)) {
      setErrorMessage('Please enter a valid email');
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setErrorMessage('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    // Build signup metadata before the Firebase call so that fetchUserData,
    // triggered by onAuthStateChanged, reads it from localStorage and passes
    // the full additionalData to createOrFetchUser — preventing a race where
    // FirebaseProvider creates the user record without the signup metadata.
    let fromParam;
    const { state: locationState } = history.location;
    if (locationState?.from) {
      const {
        pathname: fromPathname,
        search: fromSearch,
        hash: fromHash,
      } = locationState.from;

      fromParam = `${fromPathname}${fromSearch && `?${fromSearch}`}${fromHash}`;
    }

    const additionalData = {
      [REGISTER_OPTIN_PARAM]: optIn,
      [REGISTER_SIGNUP_PARAM]: true,
      [REGISTER_FROM_PARAM]: fromParam,
      [REGISTER_SOURCE_PARAM]: isExternalEmbedded ? SOURCE.EMBED : SOURCE.APP,
      [REGISTER_TOPIC_PARAM]: signUpTopic(),
      [REGISTER_ORIGIN_URL_PARAM]: getReferrerOrigin(),
      [REGISTER_PARTNER_ID_PARAM]: storeConfig?.id,
      [REGISTER_PARTNER_NAME_PARAM]: storeConfig?.title,
    };

    safeStorage.setItem('signUp_method', SIGNUP_METHOD.MANUAL);
    safeStorage.setItem('optIn', optIn);
    safeStorage.setItem('pending_signup_data', JSON.stringify(additionalData));

    try {
      await firebase.auth()
        .createUserWithEmailAndPassword(email.toLowerCase(), password);

      await createOrFetchUser(
        email.toLowerCase(),
        SIGNUP_METHOD.MANUAL,
        optIn,
      );

      try {
        await sendEmailVerification({
          returnUrl: getReturnPath(),
        });
      } catch (emailVerificationError) {
        console.error('Error sending email verification:', emailVerificationError);
      }

      setIsLoading(false);
      setView(viewType.VIEW_VERIFY_EMAIL);
      startResendCountdown();
    } catch (error) {
      safeStorage.removeItem('pending_signup_data');
      setIsLoading(false);

      if (error.code === 'auth/email-already-in-use') {
        setErrorMessage('This email is already registered. Please sign in instead.');
        return;
      }

      if (isAuthCredentialsError(error)) {
        setErrorMessage(
          'Invalid email or password. Please check your credentials and try again.',
        );
        return;
      }

      if (isTooManyRequestsError(error)) {
        setErrorMessage('Too many attempts. Please try again later.');
        return;
      }

      setErrorMessage(getGenericError());
    }
  }, [
    email,
    password,
    confirmPassword,
    optIn,
    history,
    isExternalEmbedded,
    storeConfig,
    signUpTopic,
    startResendCountdown,
  ]);

  const handleConfirmEmail = useCallback(() => {
    if (!email) {
      setErrorMessage('Please enter your email');
      return;
    }

    signInWithEmailLink(email);
  }, [email, signInWithEmailLink]);

  const handleResetPassword = useCallback(async () => {
    if (!email) {
      setErrorMessage('Please enter your email');
      return;
    }
    try {
      setIsLoading(true);
      const { error: resetError } = await sendPasswordResetEmail({
        email,
        returnUrl: getReturnPath(),
      });
      if (resetError) {
        throw resetError;
      }
      setModalContent({
        image: IconEnvelope,
        title: 'PASSWORD RESET EMAIL SENT',
        subtitle: `We sent a password reset link to ${email}. Check your inbox and follow the instructions.`,
        cta: {
          text: 'BACK TO LOGIN',
          onClick: () => {
            setErrorMessage('');
            setModalContent(MODAL_CONTENT_DEFAULT);
            setView(viewType.VIEW_EMAIL_FORM);
          },
        },
      });
    } catch (err) {
      setErrorMessage(getGenericError());
    } finally {
      setIsLoading(false);
    }
  }, [email]);

  const handleContactSupport = useCallback(() => {
    window.location.href = SUPPORT_URL;
  }, []);

  const isActiveView = useCallback((viewId) => (
    viewId === view
  ), [view]);

  useEffect(() => {
    if (onViewChange) {
      onViewChange(view);
    }
  }, [onViewChange, view]);

  useEffect(() => {
    if (view === viewType.VIEW_FORCE_RESET_PASSWORD) {
      setPassword('');
      setConfirmPassword('');
      setErrorMessage('');
    }
    if (view === viewType.VIEW_NAME_FORM) {
      setFirstName('');
      setLastName('');
      setErrorMessage('');
    }
  }, [view]);

  useEffect(() => {
    if (view !== viewType.VIEW_FORCE_RESET_PASSWORD) {
      return undefined;
    }

    const SESSION_TIMEOUT_MS = 4 * 60 * 1000; // 4 minutes

    sessionTimeoutAbortRef.current = false;

    sessionTimeoutRef.current = setTimeout(async () => {
      if (isForceResetSubmittingRef.current) {
        await new Promise(resolve => {
          const poll = setInterval(() => {
            if (!isForceResetSubmittingRef.current) {
              clearInterval(poll);
              resolve();
            }
          }, 100);
        });
      }

      if (sessionTimeoutAbortRef.current) {
        return;
      }

      try {
        await firebase.auth().signOut();
      } catch (error) {
        console.log(error ?? 'Error signing out user after session timeout');
      }
      setView(viewType.VIEW_SESSION_TIMEOUT);
    }, SESSION_TIMEOUT_MS);

    return () => {
      sessionTimeoutAbortRef.current = true;
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
        sessionTimeoutRef.current = null;
      }
    };
  }, [view]);

  useEffect(() => {
    // Email link handling
    if (firebase.auth().isSignInWithEmailLink(window.location.href)) {
      setIsLoading(true);

      const emailForSignIn = safeStorage.getItem(LOCAL_STORAGE_EMAIL_KEY);

      if (!emailForSignIn) {
        setView(viewType.VIEW_LINK_CONFIRM);
        setIsLoading(false);
      } else {
        setView(viewType.VIEW_LINK_SENT);
        setEmail(emailForSignIn);

        // Params handling
        const query = new URLSearchParams(search);
        const signedAt = getSearchParam(query, REGISTER_SIGNED_AT_PARAM);

        if (signedAt && moment(signedAt).add(10, 'minutes').isBefore(moment())) {
          setLinkError({
            image: IconHourglass,
            title: 'THIS LOGIN LINK HAS EXPIRED',
            cta: {
              text: 'SEND A NEW LINK',
              onClick: handleRequestNewLink,
            },
          });
          setIsLoading(false);
        } else {
          signInWithEmailLink(emailForSignIn);
        }
        // End of params handling
      }
    }
    // End of email link handling

    return () => {
      validRef.current = null;
      phoneValidRef.current = null;
      if (resendTimerRef.current) {
        clearInterval(resendTimerRef.current);
      }
      if (phoneOtpOwnedRef.current) {
        clearRecaptchaVerifier();
        resumeAuthListener();
        phoneOtpOwnedRef.current = false;
      }
    };
  }, []);

  // Poll for email verification status while on the verify email screen
  useEffect(() => {
    if (!isActiveView(viewType.VIEW_VERIFY_EMAIL)) {
      return undefined;
    }
    let isCancelled = false;

    const checkEmailVerified = async () => {
      const { currentUser } = firebase.auth();
      if (!currentUser || isCheckingVerificationRef.current || isCancelled) {
        return;
      }

      isCheckingVerificationRef.current = true;

      try {
        // Reload user to get fresh emailVerified status from Firebase
        await currentUser.reload();
        if (isCancelled) {
          return;
        }

        if (currentUser.emailVerified) {
          // Force refresh the ID token to include email_verified: true claim
          await currentUser.getIdToken(true);
          if (isCancelled) {
            return;
          }
          // Mark token as refreshed so next API call sends x-token-refreshed header
          markTokenAsRefreshed();

          // Complete the signup flow - trigger redirect via parent
          const userEmail = email || currentUser.email || '';
          handleFinish(true, userEmail.toLowerCase(), SIGNUP_METHOD.MANUAL);
          // Change view to unblock the parent's redirect
          setView(viewType.VIEW_EMAIL_FORM);
        }
      } catch (error) {
        console.error('Error checking email verification status:', error);
      } finally {
        isCheckingVerificationRef.current = false;
      }
    };

    // Check immediately and then every 5 seconds
    checkEmailVerified();
    const interval = setInterval(checkEmailVerified, 5000);

    return () => {
      isCancelled = true;
      clearInterval(interval);
    };
  }, [view, handleFinish, setView]);

  // Cross-tab coordination for the name form — if another tab completes
  // or skips the name form, this tab should proceed too.
  useEffect(() => {
    if (!isActiveView(viewType.VIEW_NAME_FORM)) return undefined;

    let channel;
    try {
      channel = new BroadcastChannel(NAME_FORM_CHANNEL_NAME);
    } catch {
      return undefined;
    }

    channel.onmessage = (event) => {
      const { type } = event.data || {};
      if (type === NAME_FORM_MSG.COMPLETED) {
        onNameFormDone?.();
        setView(viewType.VIEW_EMAIL_FORM);
      } else if (type === NAME_FORM_MSG.SKIPPED) {
        onNameFormSkipped?.();
        setView(viewType.VIEW_EMAIL_FORM);
      }
    };

    return () => channel.close();
  }, [view, onNameFormDone, onNameFormSkipped]);

  if (
    (isActiveView(viewType.VIEW_LINK_SENT) && loading && !linkError.title) ||
    (isActiveView(viewType.VIEW_EMAIL_FORM) && isParentLoading)
  ) {
    return (
      <LoadingBar className="GlobalLoginV2__loader" />
    );
  }

  return (
    <div className={classNames('GlobalLoginV2', { isEmbedded, isExternalEmbedded })}>
      {showLogo && !isActiveView(viewType.VIEW_LINK_SENT) && (
        <div className="GlobalLoginV2__banner" />
      )}
      <div className="GlobalLoginV2__content">
        {isActiveView(viewType.VIEW_EMAIL_FORM) && (
          isNewUser ? (
            // New user: Create account flow (password-based only)
            <>
              <h2 className="GlobalLoginV2__content--title">
                Create your free account
              </h2>
              <p className="GlobalLoginV2__content--subtitle">
                To unlock all the benefits of Trashie
              </p>
              <div className="GlobalLoginV2__content--form">
                <Input
                  type="text"
                  id="email"
                  label="Email"
                  placeholder="kate@gmail.com"
                  value={email}
                  onChange={handleOnEmailChange}
                  errorMessage={errorMessage}
                  disabled={loading}
                />
                <OptIn
                  className="GlobalLoginV2__content--form-optIn"
                  checked={optIn}
                  onChange={() => setOptIn(!optIn)}
                  disabled={loading}
                />
                <Button
                  type={isExternalEmbedded ? BUTTON_TYPE.PRIMARY : BUTTON_TYPE.QUATERNARY}
                  size={BUTTON_SIZE.LARGE}
                  onClick={handleContinueToSignup}
                  style={getNextButtonStyles(storeConfig)}
                  disabled={loading}
                >
                  {loading ? <DotsLoader /> : 'Create Account'}
                </Button>
              </div>
            </>
          ) : (
            // Default login flow (returning user or no email entered yet)
            <>
              <h2 className="GlobalLoginV2__content--title">
                HOW DO YOU WANT TO LOGIN?
              </h2>
              <div className="GlobalLoginV2__content--social-buttons">
                <button
                  className="GlobalLoginV2__content--social-btn"
                  type="button"
                  onClick={() => handleSignInWithGoogle()}
                  {...{
                    [POSTHOG_CAPTURE_ATTRIBUTES.TRACKER_ID]: getTrackerId(trackerGoogleButton),
                  }}
                >
                  <img src={GoogleLogo} alt="Google" />
                </button>
              </div>
              <div className="GlobalLoginV2__content--form">
                <Input
                  type="text"
                  id="email"
                  label="Email"
                  placeholder="kate@gmail.com"
                  value={email}
                  onChange={handleOnEmailChange}
                  errorMessage={errorMessage}
                  disabled={loading}
                />
                {emailFormat(email) && (
                  isVerifyingUserEmail ? (
                    <DotsLoader className="GlobalLoginV2__content--dotsLoader" />
                  ) : (
                    isUserEmail && (
                      <Button
                        type={isExternalEmbedded
                          ? BUTTON_TYPE.SECONDARY
                          : BUTTON_TYPE.QUATERNARY}
                        size={BUTTON_SIZE.LARGE}
                        onClick={handleContinueWithPassword}
                        style={getNextButtonStyles(storeConfig)}
                        disabled={loading}
                        trackerProps={{
                          [POSTHOG_CAPTURE_ATTRIBUTES.TRACKER_ID]:
                            getTrackerId(trackerPasswordButton),
                        }}
                      >
                        CONTINUE WITH PASSWORD
                      </Button>
                    )
                  )
                )}
                <div className="GlobalLoginV2__content--divider">
                  <span className="GlobalLoginV2__content--divider-line" />
                  <span className="GlobalLoginV2__content--divider-text">or</span>
                  <span className="GlobalLoginV2__content--divider-line" />
                </div>
                <div className="GlobalLoginV2__phoneInput">
                  <p className="InputNew__label">Phone number</p>
                  <div className="GlobalLoginV2__phoneInput--wrapper">
                    <span className="GlobalLoginV2__phoneInput--prefix">+1</span>
                    <input
                      className="GlobalLoginV2__phoneInput--field"
                      type="tel"
                      id="phone"
                      value={phone}
                      onChange={(e) => handleOnPhoneChange(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                </div>
                {phoneLoginError && (
                  <p className="GlobalLoginV2__content--inline-error">
                    {phoneLoginError}
                  </p>
                )}
                {isValidPhone(phone) && (
                  isVerifyingUserPhone ? (
                    <DotsLoader className="GlobalLoginV2__content--dotsLoader" />
                  ) : (
                    isUserPhone && (
                      <Button
                        type={isExternalEmbedded ? BUTTON_TYPE.PRIMARY : BUTTON_TYPE.QUATERNARY}
                        size={BUTTON_SIZE.LARGE}
                        onClick={handlePhoneLogin}
                        style={getNextButtonStyles(storeConfig)}
                        disabled={loading}
                      >
                        {loading ? <DotsLoader /> : 'CONTINUE WITH PHONE NUMBER'}
                      </Button>
                    )
                  )
                )}
                <OptIn
                  className="GlobalLoginV2__content--form-optIn"
                  checked={optIn}
                  onChange={() => setOptIn(!optIn)}
                  disabled={loading || isVerifyingUserEmail}
                />
              </div>
            </>
          )
        )}
        {isActiveView(viewType.VIEW_LINK_SENT) && (
          <>
            {!linkError.title && (
              <NavigationBar
                onGoBack={handleRequestNewLink}
                className={classNames('GlobalLoginV2__navigation', { isExternalEmbedded })}
              />
            )}
            <div className="GlobalLoginV2__content--form">
              {linkError.title ? (
                <MessageContent {...linkError} />
              ) : (
                <MessageContent
                  image={IconSend}
                  title="LINK SENT TO:"
                  subtitle={email}
                  message={`Click on the link in your email to complete ${isUserEmail ? 'log in' : 'sign up'}.`}
                  embedContent={isEmbedded || isExternalEmbedded}
                />
              )}
            </div>
            <div className="GlobalLoginV2__content--option">
              <p className="GlobalLoginV2__content--option-text">
                {linkError.title ? 'Still not working?' : 'Didn’t get the link?'}
              </p>
              <Button
                className="GlobalLoginV2__content--option-link"
                type={BUTTON_TYPE.LINK_QUINARY}
                style={getOptionButtonStyles(storeConfig)}
                onClick={linkError.title ? handleContactSupport : handleRequestNewLink}
              >
                {linkError.title ? 'CONTACT SUPPORT' : 'REQUEST A NEW LINK'}
              </Button>
            </div>
          </>
        )}
        {isActiveView(viewType.VIEW_SENDING_LINK) && (
          <div className="GlobalLoginV2__content--form">
            <MessageContent
              image={IconSend}
              title="SENDING LINK..."
              loader
              embedContent={isEmbedded || isExternalEmbedded}
            />
          </div>
        )}
        {isActiveView(viewType.VIEW_LINK_CONFIRM) && (
          <div className="GlobalLoginV2__content--form">
            <div className="GlobalLoginV2__content--header">
              <p className="subtitle">ENTER YOUR EMAIL TO CONTINUE:</p>
            </div>
            <div className="GlobalLoginV2__content--form">
              <Input
                type="text"
                id="email"
                placeholder="Enter your email…"
                value={email}
                onChange={handleOnEmailChange}
                errorMessage={errorMessage}
                disabled={isLoading}
              />
              {emailFormat(email) && (
                <Button
                  type={BUTTON_TYPE.QUATERNARY}
                  size={BUTTON_SIZE.LARGE}
                  onClick={handleConfirmEmail}
                  style={getNextButtonStyles(storeConfig)}
                  disabled={isLoading}
                >
                  {isLoading ? <DotsLoader /> : 'CONTINUE'}
                </Button>
              )}
            </div>
          </div>
        )}
        {isActiveView(viewType.VIEW_EMAIL_PASSWORD_FORM) && (
          <>
            <NavigationBar
              onGoBack={() => {
                setErrorMessage('');
                setPassword('');
                setView(viewType.VIEW_EMAIL_FORM);
              }}
              className={classNames('GlobalLoginV2__navigation', { isExternalEmbedded })}
            />
            <div className="GlobalLoginV2__content--form">
              <h2 className="GlobalLoginV2__content--title">
                Welcome back
              </h2>
              <Input
                type="text"
                id="userEmail"
                label="Email"
                placeholder="Email"
                value={email}
                onChange={handleOnEmailChange}
                errorMessage={Boolean(errorMessage)}
                disabled
              />
              <Input
                type="password"
                id="password"
                label="Password"
                placeholder="Password"
                value={password}
                onChange={handleOnPasswordChange}
                errorMessage={errorMessage}
                disabled={loading}
              />
              {emailFormat(email) && (
                <>
                  <Button
                    type={isExternalEmbedded ? BUTTON_TYPE.PRIMARY : BUTTON_TYPE.QUATERNARY}
                    size={BUTTON_SIZE.LARGE}
                    onClick={handleUserLogin}
                    style={getNextButtonStyles(storeConfig)}
                    disabled={loading || !password}
                  >
                    {loading ? <DotsLoader /> : 'Log in'}
                  </Button>
                  <div className="GlobalLoginV2__content--option GlobalLoginV2__content--option-row">
                    <p className="GlobalLoginV2__content--option-text">
                      Forgot Password?
                    </p>
                    <Button
                      className="GlobalLoginV2__content--option-link"
                      type={BUTTON_TYPE.LINK_QUINARY}
                      onClick={handleResetPassword}
                      style={getOptionButtonStyles(storeConfig)}
                      disabled={loading}
                    >
                      Reset Password here
                    </Button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
        {isActiveView(viewType.VIEW_EMAIL_PASSWORD_SIGNUP_FORM) && (
          <SignupForm
            email={email}
            password={password}
            confirmPassword={confirmPassword}
            errorMessage={errorMessage}
            loading={loading}
            isExternalEmbedded={isExternalEmbedded}
            storeConfig={storeConfig}
            onPasswordChange={handleOnPasswordChange}
            onConfirmPasswordChange={handleOnConfirmPasswordChange}
            onSubmit={handleSignupWithEmailPassword}
            onGoBack={() => {
              setErrorMessage('');
              setPassword('');
              setConfirmPassword('');
              setView(phone ? viewType.VIEW_PHONE_SIGNUP_EMAIL : viewType.VIEW_EMAIL_FORM);
            }}
          />
        )}
        {isActiveView(viewType.VIEW_PHONE_OTP_LOGIN) && (
          <>
            <NavigationBar
              onGoBack={handlePhoneOtpGoBack}
              className={classNames('GlobalLoginV2__navigation', { isExternalEmbedded })}
            />
            <h2 className="GlobalLoginV2__content--title">
              Enter the 6-digit code
            </h2>
            <p className="GlobalLoginV2__content--subtitle">
              {`Your code was sent to +1 ${phone.replace(/^\+?1/, '').replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}`}
            </p>
            <InputPin
              pinValues={pinValues}
              setPinValues={setPinValues}
              isValidPin={isValidPin}
              errorMessage={!isValidPin ? errorMessage : ''}
            />
            {errorMessage && isValidPin && (
              <p className="GlobalLoginV2__content--inline-error">
                {errorMessage}
              </p>
            )}
            <div className="GlobalLoginV2__content--form">
              <Button
                type={isExternalEmbedded ? BUTTON_TYPE.PRIMARY : BUTTON_TYPE.QUATERNARY}
                size={BUTTON_SIZE.LARGE}
                onClick={handlePhoneOtpValidate}
                style={getNextButtonStyles(storeConfig)}
                disabled={loading || pinValues.join('').length !== 6}
              >
                {loading ? <DotsLoader /> : 'Continue'}
              </Button>
            </div>
            {resendCountdown > 0 ? (
              <p className="GlobalLoginV2__content--verify-countdown">
                {`REQUEST NEW CODE IN ${resendCountdown} SECONDS`}
              </p>
            ) : (
              <Button
                className="GlobalLoginV2__content--option-link"
                type={BUTTON_TYPE.LINK_QUINARY}
                style={getOptionButtonStyles(storeConfig)}
                onClick={handlePhoneOtpResend}
                disabled={loading}
              >
                Request a New Code
              </Button>
            )}
            <div className="GlobalLoginV2__content--option">
              <Button
                className="GlobalLoginV2__content--option-link"
                type={BUTTON_TYPE.LINK_QUINARY}
                style={getOptionButtonStyles(storeConfig)}
                onClick={handlePhoneOtpGoBack}
                disabled={loading}
              >
                Change number
              </Button>
            </div>
          </>
        )}
        {isActiveView(viewType.VIEW_PHONE_SIGNUP) && (
          <>
            <NavigationBar
              onGoBack={handlePhoneGoBack}
              className={classNames('GlobalLoginV2__navigation', { isExternalEmbedded })}
            />
            <h2 className="GlobalLoginV2__content--title">
              Create your free account
            </h2>
            <p className="GlobalLoginV2__content--subtitle">
              To unlock all the benefits of Trashie
            </p>
            <div className="GlobalLoginV2__content--form">
              <Input
                type="tel"
                id="signupPhone"
                label="Phone number"
                value={phone}
                disabled
              />
              <OptIn
                className="GlobalLoginV2__content--form-optIn"
                checked={optIn}
                onChange={() => setOptIn(!optIn)}
                disabled={loading}
              />
              <Button
                type={isExternalEmbedded ? BUTTON_TYPE.PRIMARY : BUTTON_TYPE.QUATERNARY}
                size={BUTTON_SIZE.LARGE}
                onClick={handlePhoneCreateAccount}
                style={getNextButtonStyles(storeConfig)}
                disabled={loading}
              >
                {loading ? <DotsLoader /> : 'Create Account'}
              </Button>
            </div>
            <div className="GlobalLoginV2__content--option">
              <p className="GlobalLoginV2__content--option-text">
                Have an account?
              </p>
              <Button
                className="GlobalLoginV2__content--option-link"
                type={BUTTON_TYPE.LINK_QUINARY}
                style={getOptionButtonStyles(storeConfig)}
                onClick={handlePhoneLinkAccount}
              >
                Link your Phone Number
              </Button>
            </div>
          </>
        )}
        {isActiveView(viewType.VIEW_PHONE_SIGNUP_EMAIL) && (
          <>
            <NavigationBar
              onGoBack={() => {
                setErrorMessage('');
                setEmail('');
                setIsUserEmail(false);
                setView(viewType.VIEW_PHONE_SIGNUP);
              }}
              className={classNames('GlobalLoginV2__navigation', { isExternalEmbedded })}
            />
            <h2 className="GlobalLoginV2__content--title">
              Create your free account
            </h2>
            <p className="GlobalLoginV2__content--subtitle">
              To unlock all the benefits of Trashie
            </p>
            <div className="GlobalLoginV2__content--form">
              <Input
                type="text"
                id="signupEmail"
                label="Email"
                placeholder="kate@gmail.com"
                value={email}
                onChange={handleOnEmailChange}
                errorMessage={errorMessage}
                disabled={loading}
              />
              {emailFormat(email) && isVerifyingUserEmail && (
                <DotsLoader className="GlobalLoginV2__content--dotsLoader" />
              )}
              {isUserEmail && emailFormat(email) && !isVerifyingUserEmail && (
                <p className="GlobalLoginV2__content--inline-error">
                  This email is already registered. Please sign in instead.
                </p>
              )}
              <OptIn
                className="GlobalLoginV2__content--form-optIn"
                checked={optIn}
                onChange={() => setOptIn(!optIn)}
                disabled={loading}
              />
              <Button
                type={isExternalEmbedded ? BUTTON_TYPE.PRIMARY : BUTTON_TYPE.QUATERNARY}
                size={BUTTON_SIZE.LARGE}
                onClick={handleContinueToSignup}
                style={getNextButtonStyles(storeConfig)}
                disabled={loading || !emailFormat(email) || isVerifyingUserEmail || isUserEmail}
              >
                {loading || isVerifyingUserEmail ? <DotsLoader /> : 'Create Account'}
              </Button>
            </div>
          </>
        )}
        {isActiveView(viewType.VIEW_PHONE_LINK_LOGIN) && (
          <>
            <NavigationBar
              onGoBack={() => {
                setErrorMessage('');
                setEmail('');
                setPassword('');
                setView(viewType.VIEW_PHONE_SIGNUP);
              }}
              className={classNames('GlobalLoginV2__navigation', { isExternalEmbedded })}
            />
            <h2 className="GlobalLoginV2__content--title">
              Log in to link your phone
            </h2>
            <div className="GlobalLoginV2__content--form">
              <Input
                type="text"
                id="linkEmail"
                label="Email"
                placeholder="kate@gmail.com"
                value={email}
                onChange={handleOnEmailChange}
                errorMessage={errorMessage && !emailFormat(email) ? errorMessage : ''}
                disabled={loading}
              />
              <Input
                type="password"
                id="linkPassword"
                label="Password"
                placeholder="Password"
                value={password}
                onChange={handleOnPasswordChange}
                errorMessage={errorMessage && emailFormat(email) ? errorMessage : ''}
                disabled={loading}
              />
              <OptIn
                className="GlobalLoginV2__content--form-optIn"
                checked={optIn}
                onChange={() => setOptIn(!optIn)}
                disabled={loading}
              />
              <Button
                type={isExternalEmbedded ? BUTTON_TYPE.PRIMARY : BUTTON_TYPE.QUATERNARY}
                size={BUTTON_SIZE.LARGE}
                onClick={handlePhoneLinkLogin}
                style={getNextButtonStyles(storeConfig)}
                disabled={loading || !email || !password}
              >
                {loading ? <DotsLoader /> : 'Log in'}
              </Button>
            </div>
          </>
        )}
        {isActiveView(viewType.VIEW_VERIFY_EMAIL) && (
          <div className="GlobalLoginV2__content--verify">
            <h2 className="GlobalLoginV2__content--title">
              VERIFY YOUR EMAIL
            </h2>
            <div className="GlobalLoginV2__content--verify-sent">
              <img
                className="GlobalLoginV2__content--verify-icon"
                src={IconMail}
                alt="Mail"
              />
              <span className="GlobalLoginV2__content--verify-sent-text">
                <strong>Link sent to:</strong>
                {' '}
                {email || firebase.auth().currentUser?.email || ''}
              </span>
            </div>
            <div className="GlobalLoginV2__content--divider">
              <span className="GlobalLoginV2__content--divider-line" />
            </div>
            <p className="GlobalLoginV2__content--verify-message">
              To continue, please click the verification link sent to your email.
              You will be able to continue where you left off, no worries!
            </p>
            <div className="GlobalLoginV2__content--verify-tip">
              <img
                className="GlobalLoginV2__content--verify-tip-icon"
                src={TrashieEyesRight}
                alt="Trashie Tip"
              />
              <div className="GlobalLoginV2__content--verify-tip-text">
                <strong>Trashie Tip:</strong>
                <p>
                  If you don&apos;t see our email, check the spam and promotions
                  folder just in case!
                </p>
              </div>
            </div>
            {resendCountdown > 0 ? (
              <p className="GlobalLoginV2__content--verify-countdown">
                {`REQUEST NEW LINK IN ${resendCountdown} SECONDS`}
              </p>
            ) : (
              <Button
                type={BUTTON_TYPE.QUATERNARY}
                size={BUTTON_SIZE.LARGE}
                onClick={handleResendVerificationEmail}
              >
                Request a New Link
              </Button>
            )}
            <Button
              type={BUTTON_TYPE.QUINARY}
              size={BUTTON_SIZE.LARGE}
              onClick={handleChangeEmail}
            >
              Change Email
            </Button>
          </div>
        )}
        {isActiveView(viewType.VIEW_PHONE_VERIFICATION) && (
          <PhoneVerification
            initialPhone={signupPhoneForVerification}
            onComplete={() => {
              safeStorage.removeItem(SIGNUP_PHONE_STORAGE_KEY);
              onPhoneVerificationDone?.();
              setView(viewType.VIEW_EMAIL_FORM);
            }}
            onSkip={() => {
              safeStorage.removeItem(SIGNUP_PHONE_STORAGE_KEY);
              onPhoneVerificationSkipped?.();
              setView(viewType.VIEW_EMAIL_FORM);
            }}
          />
        )}
        {isActiveView(viewType.VIEW_NAME_FORM) && (
          <>
            <h2 className="GlobalLoginV2__content--title">
              WHAT SHOULD WE CALL YOU?
            </h2>
            <div className="GlobalLoginV2__content--form">
              <Input
                type="text"
                id="firstName"
                placeholder="Preferred name"
                value={firstName}
                onChange={setFirstName}
                disabled={loading}
              />
              <Input
                type="text"
                id="lastName"
                placeholder="Last name"
                value={lastName}
                onChange={setLastName}
                errorMessage={errorMessage}
                disabled={loading}
              />
              <Button
                type={isExternalEmbedded ? BUTTON_TYPE.PRIMARY : BUTTON_TYPE.QUATERNARY}
                size={BUTTON_SIZE.LARGE}
                onClick={handleNameFormFinish}
                style={getNextButtonStyles(storeConfig)}
                disabled={loading || !firstName.trim()}
              >
                {loading ? <DotsLoader /> : 'FINISH'}
              </Button>
            </div>
            <div className="GlobalLoginV2__content--option">
              <Button
                className="GlobalLoginV2__content--option-link"
                type={BUTTON_TYPE.LINK_QUINARY}
                style={getOptionButtonStyles(storeConfig)}
                onClick={() => {
                  try {
                    const ch = new BroadcastChannel(NAME_FORM_CHANNEL_NAME);
                    ch.postMessage({ type: NAME_FORM_MSG.SKIPPED });
                    ch.close();
                  } catch { /* ignore */ }
                  onNameFormSkipped?.();
                  setView(viewType.VIEW_EMAIL_FORM);
                }}
                disabled={loading}
              >
                Skip for now
              </Button>
            </div>
          </>
        )}
        {isActiveView(viewType.VIEW_FORCE_RESET_PASSWORD) && (
          <>
            <h2 className="GlobalLoginV2__content--title">
              Security Password Reset
            </h2>
            <p className="GlobalLoginV2__content--subtitle">
              To keep your account secure,
              we are rolling out security measures to keep all our users safe.
            </p>
            <div className="GlobalLoginV2__content--form">
              <Input
                type="password"
                id="newPassword"
                label="Password"
                placeholder="Password"
                value={password}
                onChange={handleOnPasswordChange}
                errorMessage={Boolean(errorMessage)}
                disabled={loading}
              />
              <Input
                type="password"
                id="confirmNewPassword"
                label="Confirm Password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={handleOnConfirmPasswordChange}
                errorMessage={errorMessage}
                disabled={loading}
              />
              <Button
                type={isExternalEmbedded ? BUTTON_TYPE.PRIMARY : BUTTON_TYPE.QUATERNARY}
                size={BUTTON_SIZE.LARGE}
                onClick={handleForceResetPassword}
                style={getNextButtonStyles(storeConfig)}
                disabled={loading || !password || !confirmPassword}
              >
                {loading ? <DotsLoader /> : 'Continue'}
              </Button>
            </div>
            <div className="GlobalLoginV2__content--option">
              <Button
                className="GlobalLoginV2__content--option-link"
                type={BUTTON_TYPE.LINK_QUINARY}
                style={getOptionButtonStyles(storeConfig)}
                onClick={handleChangeEmail}
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </>
        )}
        {isActiveView(viewType.VIEW_SESSION_TIMEOUT) && (
          <div className="GlobalLoginV2__content--form">
            <h2 className="GlobalLoginV2__content--title">
              Session timeout
            </h2>
            <p className="GlobalLoginV2__content--subtitle">
              For security reasons, after a prolonged inactivity on a password reset screen
              we had to log you out. Please log in again to continue with setting your new password.
            </p>
            <Button
              type={isExternalEmbedded ? BUTTON_TYPE.PRIMARY : BUTTON_TYPE.QUATERNARY}
              size={BUTTON_SIZE.LARGE}
              onClick={handleChangeEmail}
              style={getNextButtonStyles(storeConfig)}
            >
              Log In Again
            </Button>
          </div>
        )}
      </div>
      {!isActiveView(viewType.VIEW_SENDING_LINK) &&
        !isActiveView(viewType.VIEW_LINK_SENT) &&
        !isActiveView(viewType.VIEW_LINK_CONFIRM) &&
        !isActiveView(viewType.VIEW_SESSION_TIMEOUT) &&
        (
          <DisclaimerPublic
            termsUrl={TERMS_OF_SERVICE_URL}
            policyUrl={PRIVACY_POLICY_URL}
          />
        )}
      <GeneralModal
        showModal={modalContent.title}
        showModalHeader={false}
        showModalFooter={false}
        canClose={false}
      >
        <MessageContent {...modalContent} />
      </GeneralModal>
    </div>
  );
};

export default GlobalLogin;
